/*
 * The wallet page shows a top-up receipt when the payment gateway sends the payer back to
 * /adopay/wallet/:walletId/details/<successful|failed|pending>?paymentNumber=..&amount=..&currency=..
 *
 * "pending" means the gateway took the money but the wallet is not credited yet; the backend
 * retries the credit, so the receipt must not say the payment failed.
 */
export type ReceiptMode = 'Successful' | 'Failed' | 'Pending'

export interface ReceiptDetails {
  paymentNumber: string
  amount: string
  currency: string
}

export interface Receipt {
  mode: ReceiptMode
  details: ReceiptDetails
}

const MODES: Record<string, ReceiptMode> = {
  successful: 'Successful',
  failed: 'Failed',
  pending: 'Pending'
}

const RECEIPT_SEGMENT = /\/(successful|failed|pending)\/?$/

/** The receipt the location asks for, or null when it asks for none. */
export function receiptFromLocation(
  pathname: string,
  search: string
): Receipt | null {
  const match = RECEIPT_SEGMENT.exec(pathname)
  if (!match) {
    return null
  }
  // The query comes from the address bar: keep only values of the expected shape.
  const query = new URLSearchParams(search)
  const value = (name: string, shape: RegExp): string => {
    const raw = query.get(name) ?? ''
    return shape.test(raw) ? raw : ''
  }

  return {
    mode: MODES[match[1]],
    details: {
      paymentNumber: value('paymentNumber', /^\d{1,20}$/),
      amount: value('amount', /^\d{1,15}(\.\d{1,2})?$/),
      currency: value('currency', /^[A-Z]{3}$/)
    }
  }
}

/** The wallet page's own path once the receipt is closed: the receipt segment removed. */
export function pathWithoutReceipt(pathname: string): string {
  return pathname.replace(RECEIPT_SEGMENT, '')
}

/** The wallet page's path showing the given receipt instead of the current one. */
export function receiptPath(
  pathname: string,
  segment: 'successful' | 'failed' | 'pending'
): string {
  return `${pathWithoutReceipt(pathname)}/${segment}`
}
